import { useEffect, useState } from 'react';
import { keyIsTab } from "../../shared/utils/keyPressEvent/keyPressEvent";
const focusableElement = ['a', 'button', 'textarea', 'input', 'select', 'iframe'];
/**
 * A hook that applies a focus trap on a specified element
 *
 * @param containerRef a ref to the container element that the focus trap is applied to
 * @param focusableElementQueryArray an array of selectors to be queried which are allowed to be focused on
 * @param focusedElementIndex the index of the element that should initially have focus.
 * If this is not defined, the first focusable element will have focus by default
 *
 * @returns a function that resets the focus trap. This can be useful if the focusable elements
 * inside the container changes, but the focusableElementQueryArray stays the same
 */
export const useFocusTrap = (containerRef, focusableElementQueryArray = focusableElement, focusedElementIndex) => {
    const [refresh, setRefresh] = useState(0);
    const refreshFocusableElements = () => setRefresh((previousValue) => previousValue + 1);
    const targetElement = containerRef?.current;
    useEffect(() => {
        let firstFocusElement = null;
        let lastFocusElement = null;
        function handleTabKeyPress(event) {
            if (keyIsTab(event)) {
                if (!event.shiftKey && document.activeElement === lastFocusElement) {
                    event.preventDefault();
                    firstFocusElement?.focus();
                }
                else if (event.shiftKey && document.activeElement === firstFocusElement) {
                    event.preventDefault();
                    lastFocusElement?.focus();
                }
            }
        }
        if (focusableElementQueryArray.length) {
            const focusableElementQuery = `${focusableElementQueryArray.join(':not([tabIndex="-1"]):not([disabled]):not([aria-hidden="true"]), ')}:not([tabIndex="-1"]):not([disabled]):not([aria-hidden="true"])`;
            if (targetElement) {
                const focusableElements = targetElement.querySelectorAll(focusableElementQuery);
                if (focusableElements.length) {
                    firstFocusElement = focusableElements[0];
                    lastFocusElement = focusableElements[focusableElements.length - 1];
                    focusedElementIndex && focusableElements[focusedElementIndex]
                        ? focusableElements[focusedElementIndex].focus()
                        : firstFocusElement.focus();
                }
                targetElement.addEventListener('keydown', handleTabKeyPress);
            }
        }
        return () => {
            targetElement?.removeEventListener('keydown', handleTabKeyPress);
        };
    }, [focusableElementQueryArray, refresh, targetElement]);
    return refreshFocusableElements;
};
